import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MuiTooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { IFormField } from '@opengov/forms-library';
import { IProposalDetails, IBudgetProposals } from '@opengov/stories-shared/types';
import { getMergeTagId } from '@opengov/stories-shared/utils';
import React, { useState } from 'react';

import { IEditorMessages, IStoryViewMessages } from '../../../../../../types';
import {
  createMergeTagHtml,
  MAX_NUMBER_OF_MERGE_TAGS,
  useMergeTagsContext
} from '../../../../../common/context/MergeTagsContext';
import { EditorCommand, useCurrentEditorContext } from '../../../../StoryEditor/CurrentEditorContext';

import { InsertIcon } from './InsertIcon';

import { styles } from './ProposalsContentView.styles';

export type IProposalsContentViewProps = {
  messages: IStoryViewMessages;
  editorMessages: IEditorMessages;
  budgetId: number;
  proposal: IBudgetProposals;
  proposalFieldsDetail: IProposalDetails | null | undefined;
  entitySubdomain: string;
  storyId: string;
  onReplaceProposalClick(proposal: IBudgetProposals): void;
  onRemoveProposalClick(proposalToRemove: IBudgetProposals): void;
};

const ProposalFields: React.FC<{
  budgetId: number;
  proposalId: number;
  messages: IStoryViewMessages;
  editorMessages: IEditorMessages;
  formField: IFormField;
}> = ({ budgetId, proposalId, messages, editorMessages, formField }) => {
  const { currentEditor, execCommand } = useCurrentEditorContext();
  const { mergeTags, storyMergeTagList } = useMergeTagsContext();
  const mergeTagId = getMergeTagId({ budgetId, proposalId, fieldId: formField.fieldTemplateId });
  const mergeTag = mergeTags[mergeTagId];
  const insertButtonDisabled = !currentEditor || storyMergeTagList.length >= MAX_NUMBER_OF_MERGE_TAGS;
  const maxTagsTooltip = storyMergeTagList.length >= MAX_NUMBER_OF_MERGE_TAGS
    ? editorMessages.maxNumberOfFields
    : "";

  return mergeTag ? (
    <div>
      <Divider aria-hidden={true} />
      <Box sx={styles.proposalFieldsContainer}>
        <Box sx={styles.proposalFields}>
          <Typography variant="body2">
            {formField.label}
          </Typography>
          <MuiTooltip title={mergeTag.value} placement="top">
            <Typography variant="caption" sx={styles.mergeTag} tabIndex={0}>
              {`{`}
              <span className="ellipsis">{mergeTag.value}</span>
              {`}`}
            </Typography>
          </MuiTooltip>
        </Box>
        <MuiTooltip
          title={maxTagsTooltip}
          placement="left"
          disableHoverListener={!insertButtonDisabled}
        >
          <span>
            <Button
              data-test="insertButton"
              variant="text"
              size="small"
              disabled={insertButtonDisabled}
              onClick={() =>
                execCommand({
                  command: EditorCommand.MceInsertContent,
                  content: createMergeTagHtml(mergeTag)
                })
              }
              startIcon={<InsertIcon />}
            >
              {messages.insert}
            </Button>
          </span>
        </MuiTooltip>
      </Box>
    </div>
  ) : null;
};

export const ProposalsContentView: React.FC<IProposalsContentViewProps> = ({
  entitySubdomain,
  messages,
  editorMessages,
  budgetId,
  proposal,
  proposalFieldsDetail,
  onReplaceProposalClick,
  onRemoveProposalClick
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [proposalMenuAnchor, setProposalMenuAnchor] = useState<HTMLElement | null>(null);
  const encodedBudgetId = btoa(`Budget:${budgetId}`);
  const encodedProposalId = btoa(`Proposal:[${budgetId},${proposal.proposalId}]`);
  const proposalUrl = `budgets/${entitySubdomain}/${encodedBudgetId}/proposals/${encodedProposalId}`;
  const isProposalRemoved = !proposalFieldsDetail;

  return (
    <Box data-test={`proposals-view-${proposal.proposalId}`} sx={styles.container}>
      <Box sx={styles.header}>
        <Box sx={styles.headerWithIcon}>
          <Typography variant="subtitle1">
            {!isProposalRemoved ? proposalFieldsDetail.title : messages.proposalRemoved}
          </Typography>
          {isProposalRemoved && <WarningAmberIcon sx={styles.warningIcon} aria-hidden={true} />}
        </Box>
        <IconButton
          onClick={() => setIsCollapsed(!isCollapsed)}
          sx={styles.arrowDownButton}
          data-test="expand-proposal-details"
          size="small"
          aria-label={isCollapsed ? "Expand proposal details" : "Collapse proposal details"}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? (
            <ChevronRightIcon sx={styles.iconColor} aria-hidden={true} />
          ) : (
            <ExpandMoreIcon sx={styles.iconColor} aria-hidden={true} />
          )}
        </IconButton>
      </Box>
      {!isCollapsed && (
        <Box sx={styles.proposalContainer}>
          <Divider aria-hidden={true} />
          <Box sx={styles.content}>
            <Box sx={styles.proposalActionContent}>
              <Typography variant="body2">
                {`${messages.proposals}:`}
              </Typography>
              <MuiTooltip title={messages.proposalTooltip} placement="right">
                <Box
                  component="button"
                  type="button"
                  aria-label={messages.proposalTooltip}
                  sx={styles.infoIconButton}
                >
                  <InfoOutlinedIcon sx={styles.iconColor} fontSize="small" aria-hidden={true} />
                </Box>
              </MuiTooltip>
            </Box>
            <Box sx={styles.proposalActionContent}>
              {!isProposalRemoved && (
                <MuiTooltip title={messages.openProposal} placement="right">
                  <Box
                    component="a"
                    id={`url-${proposal.proposalId}`}
                    href={`https://${location.hostname}/${proposalUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${messages.openProposal}, opens in new tab`}
                    sx={styles.externalLinkIcon}
                  >
                    <OpenInNewIcon sx={styles.iconColor} fontSize="small" aria-hidden={true} />
                  </Box>
                </MuiTooltip>
              )}
              <IconButton
                data-test="proposal-more-dropdown-option"
                size="small"
                sx={styles.moreButton}
                aria-label="Proposal options"
                aria-haspopup="true"
                aria-controls={`proposal-options-menu-${proposal.proposalId}`}
                onClick={(e) => setProposalMenuAnchor(e.currentTarget)}
              >
                <MoreVertIcon fontSize="small" aria-hidden={true} />
              </IconButton>
              <Menu
                id={`proposal-options-menu-${proposal.proposalId}`}
                anchorEl={proposalMenuAnchor}
                open={Boolean(proposalMenuAnchor)}
                onClose={() => setProposalMenuAnchor(null)}
              >
                <MenuItem
                  data-test="remove-proposal-button"
                  onClick={() => {
                    setProposalMenuAnchor(null);
                    onRemoveProposalClick(proposal);
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" aria-hidden={true} />
                  </ListItemIcon>
                  <ListItemText>{messages.remove}</ListItemText>
                </MenuItem>
                <MenuItem
                  data-test="relpace-proposal-button"
                  onClick={() => {
                    setProposalMenuAnchor(null);
                    onReplaceProposalClick(proposal);
                  }}
                >
                  <ListItemIcon>
                    <RefreshIcon fontSize="small" aria-hidden={true} />
                  </ListItemIcon>
                  <ListItemText>{messages.replace}</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Box>
          {!isProposalRemoved &&
            proposalFieldsDetail.proposalFields.map((formField) => (
              <ProposalFields
                key={formField.fieldTemplateId}
                budgetId={budgetId}
                proposalId={proposal.proposalId}
                messages={messages}
                editorMessages={editorMessages}
                formField={formField}
              />
            ))}
        </Box>
      )}
    </Box>
  );
};
